import React, { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { formatDay } from './AssignMentorDialog';

/**
 * Administrative closure of an unresolved experience. It records who closed it and why —
 * it never issues a certificate, invents family feedback or changes recorded attendance.
 */
export function AdminCloseDialog({ booking, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const feedbackCase = booking.status === 'awaiting_feedback';

  const submit = async () => {
    if (!reason.trim()) {
      toast.error('Please record why this is being closed');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/coordinator/bookings/${booking.id}/admin-close`, { reason: reason.trim() });
      toast.success('Closed administratively — no certificate was issued');
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not close this experience');
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-md max-h-[88vh] overflow-y-auto" data-testid="admin-close-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">Close administratively</DialogTitle>
          <DialogDescription>
            {booking.career_name}
            {booking.assigned_date ? ` · ${formatDay(booking.assigned_date)}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-[#4B5563] leading-relaxed" data-testid="admin-close-explainer">
            {feedbackCase
              ? 'The experience took place but the family never submitted feedback. Closing it stops the follow-up. No certificate will be issued and the feedback stays empty.'
              : 'This date has passed and the mentor never confirmed what happened. Closing it stops the follow-up. No attendance is recorded, no certificate is issued, and the experience is not marked as completed or not conducted.'}
          </p>

          <div>
            <label className="text-[11px] font-semibold text-[#1F2937]">
              Reason<span className="text-[#F06651]"> *</span>
            </label>
            <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
              placeholder={feedbackCase
                ? 'e.g. Family contacted twice, no feedback received, closing the follow-up'
                : 'e.g. Mentor unreachable since the session date, nothing recorded'}
              className="mt-1 rounded-lg border-gray-200 text-xs" data-testid="admin-close-reason" />
            <p className="text-[10px] text-[#9CA3AF] mt-1">Recorded against this experience with your name and today's date.</p>
          </div>

          <div className="flex gap-2">
            <Button size="sm" disabled={saving || !reason.trim()} onClick={submit} data-testid="admin-close-confirm-btn"
              className="rounded-lg text-xs text-white bg-[#1F2937] hover:bg-[#111827]">
              {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Archive className="h-3 w-3 mr-1" />}
              Confirm closure
            </Button>
            <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={onClose} data-testid="admin-close-cancel-btn">
              Back
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AdminCloseDialog;
